import { Box } from "@mui/system";
import KeywordsDateFilter from "./KeywordsContent/KeywordsDateFilter";
import KeywordsSearchFilter from "./KeywordsContent/KeywordsSearchFilter";
import KeywordsSearchItems from "./KeywordsContent/KeywordsSearchItems";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../../../features/userSlice";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { buiDashboardEndPointLogicMobile } from "../../../../../../../../logic/AssetManagement/overview/BusinessLeaderDashboardMobile/buiDashboardEndPointLogicMobile";
import { setKeywordsFetchedKey } from "../../../../../../../../features/businessLeaderDashboardSlice";
import { QueryIdForKeywords } from "../../../helpers/QIDs";
import KeywordTableSkeleton from "./KeywordsContent/KeywordTableSkeleton";
import { getDateRange } from "../../../helpers/getDateRangeforBrands";
import store from "../../../../../../../../store";

type SortConfig = {
  field: string | null;
  direction: "asc" | "desc";
};

const Keywords = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const websiteSharedUids = searchParams.get("workspace");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: "cost",
    direction: "desc",
  });
  const [loading, setLoading] = useState(false);
  const { keywordsFetchedKey, keywordsQueryData } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );
  const initialDate = searchParams.get("dateForKeywords") || "MTD";
  const [dateType, setDateType] = useState(initialDate);
  const initialSearch = searchParams.get("searchKeywordForKeywords");
  const [searchValue, setSearchValue] = useState<string | null>(initialSearch);

  const handleSearchChange = (value: string | null) => {
    setSearchValue(value);
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("searchKeywordForKeywords", value);
    } else {
      next.delete("searchKeywordForKeywords");
    }
    setSearchParams(next, { replace: true });
  };

  const handleDateChange = (value: string) => {
    setSearchValue(null);
    setDateType(value);
    const next = new URLSearchParams(searchParams);
    next.delete("searchKeywordForKeywords");
    next.set("dateForKeywords", value);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const fetchKey = `${websiteSharedUids}-${dateType}`;
    if (!oauthTokenHeader || !websiteSharedUids) return;
    if (keywordsFetchedKey === fetchKey) return;
    const { start_date, end_date } = getDateRange(dateType);
    const fetchData = async () => {
      try {
        setLoading(true);
        const dimensionResponse =
          await buiDashboardEndPointLogicMobile.getDimensionInBizLeaderDashboardMobile(
            oauthTokenHeader,
          );
        if (!dimensionResponse || dimensionResponse.status !== 200) {
          return;
        }
        const dimensions = dimensionResponse?.data?.data || [];
        const matchedDimensions = dimensions
          ?.filter((dim: any) =>
            dim.websites?.some(
              (site: any) => site.shared_uid === websiteSharedUids,
            ),
          )
          ?.map((dim: any) => dim.dimension);
        const dimensionsToUse =
          matchedDimensions?.length > 0
            ? matchedDimensions
            : dimensions?.length > 0
              ? [dimensions[0].dimension]
              : [];
        if (!dimensionsToUse?.length) return;
        if (!matchedDimensions?.length) return;
        if (!start_date || !end_date) {
          store.dispatch(
            setDisplayErrorSnackBar({
              message: "Date range is not valid",
              type: "error",
            }),
          );
          setLoading(false);
          return;
        }
        if (!websiteSharedUids) {
          store.dispatch(
            setDisplayErrorSnackBar({
              message: "Website shared uid is not valid",
              type: "error",
            }),
          );
          setLoading(false);
          return;
        }
        await buiDashboardEndPointLogicMobile.getKeywordsQueryInBizLeaderDashboardMobile(
          {
            oauthTokenHeader,
            queryId: QueryIdForKeywords,
            websiteSharedUids: websiteSharedUids?.split(",") || [],
            type: "site",
            dimension: dimensionsToUse || [],
            start_date,
            end_date,
          },
        );
        dispatch(setKeywordsFetchedKey(fetchKey));
      } catch (e: any) {
        dispatch(
          setDisplayErrorSnackBar({
            message:
              e?.parsedText ||
              "An error occurred while fetching keywords data. Please try again.",
            type: "error",
          }),
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteSharedUids, oauthTokenHeader, dateType, keywordsFetchedKey]);

  return (
    <>
      <Box sx={{ px: 2 }}>
        <KeywordsDateFilter
          value={dateType}
          onChange={handleDateChange}
          loading={loading}
        />
        {loading ? (
          <KeywordTableSkeleton />
        ) : keywordsQueryData?.query_output !== null ? (
          <>
            <KeywordsSearchFilter
              value={searchValue}
              onChange={handleSearchChange}
              sortConfig={sortConfig}
              setSortConfig={setSortConfig}
            />
            <KeywordsSearchItems
              searchValue={searchValue}
              sortConfig={sortConfig}
            />
          </>
        ) : null}
      </Box>
    </>
  );
};

export default Keywords;